import { useState, useEffect, useRef } from 'react';
import { imageUrl } from '../lib/images';
import { useCmsGallery } from '../hooks/useCms';

export default function GalleryLightbox({
  gallerySlug,
  staticImages = [],
  title = '',
  description = '',
  columns = 3,
}) {
  const { gallery } = useCmsGallery(gallerySlug);

  // Use CMS images if available, else staticImages fallback
  const images = gallery?.images?.length ? gallery.images : staticImages;
  const galleryTitle = gallery?.title || title;
  const galleryDesc = gallery?.description || description;

  const [activeIndex, setActiveIndex] = useState(null);
  const touchStartX = useRef(null);

  const isOpen = activeIndex !== null;

  const handleOpen = (idx) => {
    setActiveIndex(idx);
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setActiveIndex(null);
    document.body.style.overflow = 'auto';
  };

  const handlePrev = () => {
    if (images.length === 0) return;
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    if (images.length === 0) return;
    setActiveIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, images.length]);

  // Touch swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartX.current = null;
  };

  if (!images || images.length === 0) return null;

  const colClasses = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className="w-full my-8">
      {galleryTitle && (
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#1e9457] uppercase mb-1">
            <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
            Dokumentation & Galleri
          </div>
          <h3 className="text-2xl font-bold tracking-tight text-slate-900">{galleryTitle}</h3>
          {galleryDesc && <p className="text-slate-600 mt-1 text-sm max-w-2xl">{galleryDesc}</p>}
        </div>
      )}

      {/* Grid */}
      <div className={`grid ${colClasses} gap-4 sm:gap-6`}>
        {images.map((src, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => handleOpen(idx)}
            className="group relative aspect-[4/3] rounded-lg overflow-hidden bg-slate-900 shadow-sm border border-slate-200/80 focus:outline-none focus:ring-2 focus:ring-[#1e9457] text-left"
          >
            <img
              src={imageUrl(src, 'width=600,quality=85,format=auto')}
              alt={`${galleryTitle || 'Glasfiber opgave'} - Billede ${idx + 1}`}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-white tracking-wide">
                <i className="fa-solid fa-magnifying-glass-plus text-[#1e9457]"></i>
                Åbn billede i fuld størrelse
              </span>
            </div>
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-slate-900/70 text-[10px] font-mono font-medium text-slate-200 backdrop-blur-xs">
              {idx + 1} / {images.length}
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Billedvisning"
          className="fixed inset-0 z-50 bg-slate-950/95 flex flex-col justify-between p-4 sm:p-6 backdrop-blur-md animate-fadeIn"
          onClick={handleClose}
        >
          {/* Top Bar */}
          <div
            className="flex items-center justify-between text-white w-full max-w-6xl mx-auto z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-semibold tracking-wide">
              {galleryTitle} <span className="text-slate-400 font-normal">({activeIndex + 1} af {images.length})</span>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2.5 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-200 hover:text-white transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
              aria-label="Luk visning"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
          </div>

          {/* Main Image Viewport with Touch/Swipe */}
          <div
            className="relative flex-1 flex items-center justify-center max-w-6xl w-full mx-auto my-2 overflow-hidden select-none"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Prev Button */}
            {images.length > 1 && (
              <button
                type="button"
                onClick={handlePrev}
                className="absolute left-2 sm:left-4 z-20 p-3 rounded-full bg-slate-900/80 hover:bg-emerald-600 text-white transition-colors cursor-pointer border border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                aria-label="Forrige billede"
              >
                <i className="fa-solid fa-chevron-left text-lg"></i>
              </button>
            )}

            {/* Image */}
            <div className="relative max-h-full max-w-full flex items-center justify-center">
              <img
                src={imageUrl(images[activeIndex], 'width=1600,quality=90,format=auto')}
                alt={`${galleryTitle} forstørret visning`}
                className="max-h-[75vh] sm:max-h-[82vh] max-w-[90vw] object-contain rounded shadow-2xl border border-slate-800"
              />
            </div>

            {/* Next Button */}
            {images.length > 1 && (
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-2 sm:right-4 z-20 p-3 rounded-full bg-slate-900/80 hover:bg-emerald-600 text-white transition-colors cursor-pointer border border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                aria-label="Næste billede"
              >
                <i className="fa-solid fa-chevron-right text-lg"></i>
              </button>
            )}
          </div>

          {/* Thumbnail Strip / Navigation hint */}
          <div
            className="w-full max-w-6xl mx-auto z-10 flex items-center justify-center gap-2 overflow-x-auto py-2"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((src, thumbIdx) => (
              <button
                key={thumbIdx}
                type="button"
                onClick={() => setActiveIndex(thumbIdx)}
                className={`relative w-12 h-12 sm:w-16 sm:h-16 rounded overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${
                  thumbIdx === activeIndex
                    ? 'border-[#1e9457] scale-105 opacity-100 ring-2 ring-emerald-500/40'
                    : 'border-slate-700 opacity-50 hover:opacity-90'
                }`}
              >
                <img
                  src={imageUrl(src, 'width=120,quality=70,format=auto')}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
